import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import { useCollection } from 'core/hooks/useCollection';
import SharedButton from 'shared/components/buttons/button';
import { useCallback, useEffect, useState } from 'react';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import { orderBy, QueryConstraint, Timestamp, where } from 'firebase/firestore';
import { IButton } from 'shared/components/buttons/button-interface';
import dayjs from 'dayjs';
import { IRdcConsignmentDao } from 'core/api/types/rdc.interface';
import { RdcConsignmentsApiService } from 'core/api';
import { useNavigate } from 'react-router-dom';
import { useSharedData } from 'core/providers/SharedDataProvider';

const RdcTableColumns: ISharedTableColumn[] = [
  { label: 'Date', key: 'date', templateId: 'date', width: 12 },
  { label: 'Time', key: 'time', templateId: 'time', width: 8 },
  { label: 'Destination', key: 'destination', templateId: 'text', width: 25 },
  { label: 'Driver', key: 'driver', templateId: 'driver', width: 15 },
  { label: 'Photos', key: 'photos', templateId: 'photos', width: 8 },
  { label: 'Signed', key: 'signed', templateId: 'signed', width: 8 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const RdcList = () => {
  const navigate = useNavigate();
  const { drivers } = useSharedData();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<IRdcConsignmentDao>({
    apiService: RdcConsignmentsApiService,
    constraints: [...constraints, orderBy('created.at', 'desc')],
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '')
        .map((filter) => {
          if (filter.id === 'driver') filter.id = 'created.by.uid';
          if (filter.id.includes('From')) {
            const date = Timestamp.fromDate(
              dayjs(filter.value as string)
                .startOf('day')
                .toDate()
            );
            return where('created.at', '>=', date);
          } else if (filter.id.includes('To')) {
            const date = Timestamp.fromDate(
              dayjs(filter.value as string)
                .endOf('day')
                .toDate()
            );
            return where('created.at', '<=', date);
          }
          return where(filter.id, '==', filter.value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const dateTemplate = (consignment: IRdcConsignmentDao) => (
    <p>{dayjs(consignment.created.at.toDate()).format('DD/MM/YYYY')}</p>
  );

  const timeTemplate = (consignment: IRdcConsignmentDao) => (
    <p>{dayjs(consignment.created.at.toDate()).format('HH:mm')}</p>
  );

  const driverTemplate = (consignment: IRdcConsignmentDao) => <p>{consignment.created.by.fullName || '-'}</p>;

  const photosTemplate = (consignment: IRdcConsignmentDao) => <p>{consignment.photoPaths?.length ?? 0}</p>;

  const signedTemplate = (consignment: IRdcConsignmentDao) => <p>{consignment.signatureCaptured ? 'Yes' : 'No'}</p>;

  const actionCellTemplate = (consignment: IRdcConsignmentDao) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => navigate(consignment.uid)}
        type='button'
        spacing='compact'
        appearance='subtle'
        label='View'
      />
    </div>
  );

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    { template: dateTemplate, id: 'date' },
    { template: timeTemplate, id: 'time' },
    { template: driverTemplate, id: 'driver' },
    { template: photosTemplate, id: 'photos' },
    { template: signedTemplate, id: 'signed' },
    { template: actionCellTemplate, id: 'action' },
  ];

  const filters: Filter[] = [
    {
      id: 'dateFrom',
      label: 'Date From',
      date: true,
    },
    {
      id: 'dateTo',
      label: 'Date To',
      date: true,
    },
    {
      id: 'driver',
      label: 'Driver',
      options: drivers?.map((driver) => ({ value: driver.uid, label: driver.fullName })) || [],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='RDC' />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={RdcTableColumns}
        rows={tableRows}
        isLoading={loading}
        customTemplates={customTemplates}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default RdcList;
